import _ from 'underscore';
import {
  AWTLogManager, AWT_NEAR_REAL_TIME, AWTSessionState, AWTLogger,
} from '@aria/webjs-sdk';
import { Listener } from '@bingads-webui/listener';
import { defaultConfig } from './default-config';
import {
  IAriaListenerOptions, IAriaLogMessage, IAriaLogEvent, ILogTableMap,
} from '../types';

const activeListeners: AriaListener[] = [];
let flushEventAttached = false;
function registerListener(listener: AriaListener): void {
  activeListeners.push(listener);

  if (!flushEventAttached) {
    window.addEventListener('beforeunload', () => {
      activeListeners.forEach((lstnr) => lstnr.flushLogs(false));
      AWTLogManager.flush(_.noop);
    });

    flushEventAttached = true;
  }
}

/**
 * Create an instance of an Aria listener
 */
export class AriaListener extends Listener {
  defaultLogCategory: string;

  defaultLogger?: AWTLogger;

  logTableNames: ILogTableMap;

  options: IAriaListenerOptions;

  registeredForTeardown: boolean;

  logs: IAriaLogEvent[] = [];

  constructor(options: Partial<IAriaListenerOptions> = {}) {
    super();
    this.options = _.defaults(options, {
      batchSize: defaultConfig.batchSize,
      enableSessionTracking: defaultConfig.enableSessionTracking,
      flushInterval: defaultConfig.flushInterval,
      tenantToken: '',
      defaultLogCategory: '',
      logTableNames: {},
      formatters: {},
      filters: [],
    });
    this.formatters = _.defaults(this.options.formatters || {}, defaultConfig.formatters);
    this.filters = this.options.filters || [];
    this.logTableNames = this.options.logTableNames;
    this.defaultLogCategory = this.options.defaultLogCategory;
    this.registeredForTeardown = false;
  }

  /**
   * init aria listener
   * @returns {undefined} - no returns
   */
  init() {
    if (!this.options.tenantToken || this.initialized) {
      return;
    }

    this.logs = [];

    // Setup AriaTelemetry
    AWTLogManager.initialize(this.options.tenantToken);
    AWTLogManager.setTransmitProfile(AWT_NEAR_REAL_TIME);

    this.defaultLogger = AWTLogManager.getLogger(this.options.tenantToken);

    if (this.options.enableSessionTracking) {
      this.defaultLogger.logSession(AWTSessionState.Started);
    }

    if (!(AWTLogManager as any).trackedListenerRefs) {
      (AWTLogManager as any).trackedListenerRefs = { count: 0 };
    }

    setInterval(() => this.flushBatch(), this.options.flushInterval);
    registerListener(this);
    this.initialized = true;
  }

  /**
   * register instance for final flush of logs to Aria
   * @private
   * @returns {undefined} - no returns
   */
  registerForTeardown() {
    if (this.registeredForTeardown || !this.initialized) {
      return;
    }

    this.registeredForTeardown = true;
    (AWTLogManager as any).trackedListenerRefs.count += 1;
  }

  /**
   * flushes logs to Aria and tears down the logger
   * @returns {undefined} - no returns
   */
  teardown() {
    if (!this.initialized) {
      return;
    }
    this.flushAll();
    this.initialized = false;

    if (this.options.enableSessionTracking) {
      this.defaultLogger?.logSession(AWTSessionState.Ended);
    }

    if (this.registeredForTeardown && (AWTLogManager as any).trackedListenerRefs) {
      (AWTLogManager as any).trackedListenerRefs.count -= 1;
    }

    this.release();
  }

  /**
  * flushes logs to aria telemetry logger
  * @param {boolean} batchLogs - specify if the logs should be flushed in batches
  * @private
  * @returns {undefined} - no returns
   */
  flushLogs(batchLogs: boolean) {
    const useBatching = batchLogs && (this.logs.length > (this.options?.batchSize ?? defaultConfig.batchSize));
    const batchSize = useBatching ? this.options.batchSize : this.logs.length;
    const logsToFlush = this.logs.splice(0, batchSize);
    _.each(logsToFlush, (log) => {
      this.defaultLogger?.logEvent(log);
    });
  }

  /**
   * If there are no active listeners then tear down the underlying logger.
   * @private
   * @returns {undefined} - no returns
   */
  release() {
    if ((AWTLogManager as any).trackedListenerRefs?.count === 0) {
      AWTLogManager.flushAndTeardown();
    }
  }

  /**
   * writes logs to the listener
   * @param {IAriaLogMessage} logMessage - logs to be written
   * @returns {undefined} - no returns
   */
  write(logMessage: IAriaLogMessage) {
    if (!this.initialized || this.filterLogs(logMessage)) {
      return;
    }

    const categoryName = this.getCategoryName(logMessage.logCategory);
    const formattedMsg = this.formatLogs(logMessage);

    this.logs.push({
      name: this.logTableNames[categoryName].name,
      properties: formattedMsg,
    });
  }

  /**
   * @private
   * @returns {undefined} - no returns
   */
  flushAll() {
    this.flushLogs(false);
  }

  /**
   * @private
   * @returns {undefined} - no returns
   */
  flushBatch() {
    this.flushLogs(true);
  }

  /**
   * @private
   * @param {string} logCategory - logs to be written
   * @returns {string} - the validated log category
   */
  getCategoryName(logCategory: string): string {
    const isValidLogCategory = _.has(this.logTableNames, logCategory);
    return isValidLogCategory ? logCategory : this.defaultLogCategory;
  }
}
